import { ProductKeys } from '../../../common/constants/products';
import { useSelectFilterStore } from '../../store/search';

import type {
	CreateSelectFilter,
	OptionsToProducts,
	SelectFilterDepParams,
	SelectFilterDeps,
	SelectFilterMonad,
	SelectFilterOption,
	SelectFilterOptionChange,
	SelectFilterState,
	SelectFilterType,
	SelectFilterViewState,
} from './types';

export const useSelectFilter = ({
	filterId,
}: {
	filterId: string;
}):
	| [
			undefined,
			{
				clearLookupInput: () => void | Promise<void>;
				clearOptions: () => void | Promise<void>;
				loadInitialOptions: () => void | Promise<void>;
				setLookupInput: (query: string) => void | Promise<void>;
				setOption: (
					option: SelectFilterOption | SelectFilterOption[],
					customValue?: Record<string, string>,
				) => void | Promise<void>;
			},
	  ]
	| [
			{
				alwaysVisible: boolean;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				customValue: any;
				defaultIsOpen: boolean;
				isLoading: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				isLookupActive: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				isLookupDefined: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				lookupInput: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				options: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				selectedOptions: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				suggestedOptions: any;
			},
			{
				clearLookupInput: () => void | Promise<void>;
				clearOptions: () => void | Promise<void>;
				loadInitialOptions: () => void | Promise<void>;
				setLookupInput: (query: string) => void | Promise<void>;
				setOption: (
					option: SelectFilterOption | SelectFilterOption[],
					customValue?: Record<string, string>,
				) => void | Promise<void>;
			},
	  ] => {
	const [state, storeActions] = useSelectFilterStore({
		filterId,
	});

	const viewActions = {
		setOption: (
			option: SelectFilterOption | SelectFilterOption[],
			customValue?: Record<string, string>,
		) => {
			const options = Array.isArray(option) ? option : [option];
			const optionChange = options.map(
				(option) =>
					({
						value: option.value,
						isSelected: !option.isSelected,
					}) satisfies SelectFilterOptionChange,
			);

			return storeActions.setSelectFilterOption({ filterId, optionChange, customValue });
		},
		loadInitialOptions: () => storeActions.loadInitialSelectFilterOptions({ filterId }),
		setLookupInput: (query: string) => storeActions.setSelectFilterLookupInput({ filterId, query }),
		clearLookupInput: () => storeActions.setSelectFilterLookupInput({ filterId, query: '' }),
		clearOptions: () => storeActions.clearSelectFilterOptions({ filterId }),
	};

	if (!state) {
		return [, viewActions] satisfies [SelectFilterViewState | undefined, typeof viewActions];
	}

	const viewState = {
		defaultIsOpen: state.alwaysVisible === false,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		isLookupDefined: state.lookup.isDefined,
		isLoading:
			// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
			state.queryParams.isLoading || state.initialOptions.isLoading || state.lookup.isLoading,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		lookupInput: state.lookup.input,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		options: state.options,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		suggestedOptions: state.options.filter((option) => !option.isSelected && option.isSuggested),
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		selectedOptions: state.selectedOptions,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		isLookupActive: state.lookup.isActive,
		alwaysVisible: state.alwaysVisible,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		customValue: state.customValue,
	} satisfies SelectFilterViewState;

	return [viewState, viewActions] satisfies [SelectFilterViewState, typeof viewActions];
};

// This is a select filter which has access to the selected product state
export const useUniversalSelectFilter = ({
	filterId,
	optionsToProducts,
}: {
	filterId: string;
	optionsToProducts: OptionsToProducts;
}):
	| [
			undefined,
			{
				clearLookupInput: () => void | Promise<void>;
				clearOptions: () => void | Promise<void>;
				loadInitialOptions: () => void | Promise<void>;
				setLookupInput: (query: string) => void | Promise<void>;
				setOption: (option: SelectFilterOption | SelectFilterOption[]) => void | Promise<void>;
			},
	  ]
	| [
			{
				alwaysVisible: boolean;
				defaultIsOpen: boolean;
				isLoading: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				isLookupActive: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				isLookupDefined: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				lookupInput: any;
				options: any;
				// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
				selectedOptions: any;
				// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
				suggestedOptions: any;
			},
			{
				clearLookupInput: () => void | Promise<void>;
				clearOptions: () => void | Promise<void>;
				loadInitialOptions: () => void | Promise<void>;
				setLookupInput: (query: string) => void | Promise<void>;
				setOption: (option: SelectFilterOption | SelectFilterOption[]) => void | Promise<void>;
			},
	  ] => {
	const [state, storeActions] = useSelectFilterStore({
		filterId,
	});

	const viewActions = {
		setOption: (option: SelectFilterOption | SelectFilterOption[]) => {
			const options = Array.isArray(option) ? option : [option];
			const optionChange = options.map(
				(option) =>
					({
						value: option.value,
						isSelected: !option.isSelected,
					}) satisfies SelectFilterOptionChange,
			);

			return storeActions.setSelectFilterOption({ filterId, optionChange });
		},
		loadInitialOptions: () => storeActions.loadInitialSelectFilterOptions({ filterId }),
		setLookupInput: (query: string) => storeActions.setSelectFilterLookupInput({ filterId, query }),
		clearLookupInput: () => storeActions.setSelectFilterLookupInput({ filterId, query: '' }),
		clearOptions: () => storeActions.clearSelectFilterOptions({ filterId }),
	};

	if (!state) {
		return [, viewActions] satisfies [SelectFilterViewState | undefined, typeof viewActions];
	}

	const selectedProductsAvailable = state.selectedProducts.length > 0;
	const products = selectedProductsAvailable ? state.selectedProducts : state.availableProducts;

	// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
	const filteredOptions = state.options
		.filter(
			// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
			(option) =>
				option.isDefault ||
				option.isSelected ||
				optionsToProducts[option.value]?.some((product) => products.includes(product)),
		)
		// This is a temporary hack to filter out Atlas specific types, unless Atlas is the selected product
		.filter(
			// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
			(option) =>
				!(
					state.selectedProducts[0] !== ProductKeys.Atlas &&
					(option.value === 'goal' || option.value === 'tag')
				),
		)
		// Filtering out all options that are tied exclusively to allFilterExcludedProducts for universal types
		// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
		.filter((option) =>
			!selectedProductsAvailable && optionsToProducts[option.value]?.length
				? optionsToProducts[option.value].some(
						(product) => !state.allFilterExcludedProducts.includes(product),
					)
				: true,
		);

	const viewState = {
		defaultIsOpen: state.alwaysVisible === false,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		isLookupDefined: state.lookup.isDefined,
		isLoading:
			// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
			state.queryParams.isLoading || state.initialOptions.isLoading || state.lookup.isLoading,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		lookupInput: state.lookup.input,
		options: filteredOptions,
		// @ts-ignore - TS7006 TypeScript 5.9.2 upgrade
		suggestedOptions: filteredOptions.filter((option) => !option.isSelected && option.isSuggested),
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		selectedOptions: state.selectedOptions,
		// @ts-ignore - TS2339 TypeScript 5.9.2 upgrade
		isLookupActive: state.lookup.isActive,
		alwaysVisible: state.alwaysVisible,
	} satisfies SelectFilterViewState;

	return [viewState, viewActions] satisfies [SelectFilterViewState, typeof viewActions];
};

export const SelectFilter: SelectFilterType = ({ initial, state, config }) => {
	const filterState = Object.freeze({
		...state,
	});

	return {
		id: initial.id,
		type: filterState.type,
		getState: getState({ initial, state: filterState, config }),
		queryParams: {
			key: initial.queryParams.key,
			get: getQueryParams({ initial, state: filterState, config }),
			set: setQueryParams({ initial, state: filterState, config }),
		},
		loadInitialOptions: loadInitialOptions({ initial, state: filterState, config }),
		setOption: setOption({ initial, state: filterState, config }),
		clearOptions: clearOptions({ initial, state: filterState, config }),
		setLookupInput: setLookupInput({ initial, state: filterState, config }),
		getOptions: getOptions({ initial, state: filterState, config }),
		getValue: getValue({ initial, state: filterState, config }),
		clear: deselectOptions({ initial, state: filterState, config }),
	};
};

export const isSelectFilter = (
	type?: SelectFilterState<string>['type'] | string,
): type is SelectFilterState<string>['type'] => {
	return type === 'single-select' || type === 'multi-select';
};

export const getState = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return (): {
        alwaysVisible: boolean; customValue?: Record<string, string>; customValueFields?: string[]; didMount: boolean; hasSelection: boolean; id: ID; initialOptions: {
            isLoading: boolean;
            options: SelectFilterOption[];
        }; lookup: {
            input: string;
            isActive: boolean;
            isDefined: boolean;
            isLoading: boolean;
            lookupResultOptions: SelectFilterOption[];
            options: SelectFilterOption[];
        }; options: SelectFilterOption[]; products: ProductKeys[]; queryParams: {
            isLoading: boolean;
            options: SelectFilterOption[];
        }; selectedOptions: SelectFilterOption[]; type: "single-select" | "multi-select"; universal: boolean; value: string[];
    } => {
		const filter = SelectFilter({ initial, state, config });
		const allOptions = filter.getOptions({ isLookupActive: false });
		const newOptions = filter.getOptions({
			isLookupActive: state.lookup.isActive,
		});
		const selectedOptions = allOptions.filter((option) => option.isSelected);

		const newState = {
			...state,
			options: newOptions,
			selectedOptions,
			hasSelection: selectedOptions.length > 0,
			value: filter.getValue(),
		} satisfies SelectFilterState<ID>;
		return newState;
	};
};

// TODO: >>>> Write more tests here on edge cases
export const setQueryParams = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return async (
		queryParams: Record<string, string[] | undefined>,
		onStateChange?: (state: SelectFilterState<string>) => void,
	): Promise<SelectFilterMonad<ID>> => {
		const queryParamValues = queryParams[initial.queryParams.key];
		const filter = SelectFilter({ initial, state, config });

		if (!queryParamValues) {
			const clearedOptions = filter.clearOptions();
			onStateChange?.(clearedOptions.getState());
			return clearedOptions;
		}

		const allCurrentOptions = filter.getOptions({ isLookupActive: false });
		const allQueryParamValues = allCurrentOptions.map((option) => option.queryParamValue);
		const queryParamsToHydrate = queryParamValues.filter(
			(value) => !allQueryParamValues.includes(value),
		);

		if (queryParamsToHydrate.length === 0) {
			const newOptions = allCurrentOptions.map(
				(option) =>
					({
						isSelected: queryParamValues.includes(option.queryParamValue),
						value: option.value,
					}) satisfies SelectFilterOptionChange,
			);

			const filterWithNewOptions = filter.setOption(newOptions);
			onStateChange?.(filterWithNewOptions.getState());
			return filterWithNewOptions;
		}

		// At this point we need to hydrate the filters with new options from
		// the query params
		const newQueryParamOptions = [...state.queryParams.options];
		const fetcher = initial.queryParams.fetcher;
		if (fetcher) {
			onStateChange?.({
				...state,
				queryParams: {
					...state.queryParams,
					isLoading: true,
				},
			});

			const fetchedOptions = await fetcher(queryParamsToHydrate, config);
			fetchedOptions.forEach((option) => {
				const mappedOption = {
					isSelected: true,
					trackingKey: option.trackingKey,
					value: option.value,
					queryParamValue: option.queryParamValue,
					isSuggested: false,
					isDefault: option.isDefault ?? false,
					avatarUrl: option.avatarUrl,
					label: option.label,
				} satisfies SelectFilterOption;
				newQueryParamOptions.push(mappedOption);
			});
		} else {
			// The edge case here is where we have an unsupported query param
			// and we don't have a fetcher to hydrate the unsupported query param
			// TODO: >>>> Not sure what to do here
		}

		const selectedOptionsFromQueryParams = newQueryParamOptions.map((option) => ({
			...option,
			isSelected: queryParamValues.includes(option.queryParamValue),
		}));

		const selectedInitialOptionsFromQueryParams = [...state.initialOptions.options].map(
			(option) => ({
				...option,
				isSelected: queryParamValues.includes(option.queryParamValue),
			}),
		);

		const hydratedQueryParams = SelectFilter({
			initial,
			state: {
				...state,
				queryParams: {
					...state.queryParams,
					options: selectedOptionsFromQueryParams,
					isLoading: false,
				},
				initialOptions: {
					...state.initialOptions,
					options: selectedInitialOptionsFromQueryParams,
				},
			},
			config,
		});

		onStateChange?.(hydratedQueryParams.getState());
		return hydratedQueryParams;
	};
};

export const getQueryParams = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return (): {
        [x: string]: string[];
    } => {
		const queryParamValue = SelectFilter({ initial, state, config })
			.getOptions({ isLookupActive: false })
			.filter((option) => option.isSelected)
			.map((option) => option.queryParamValue);

		return { [initial.queryParams.key]: queryParamValue };
	};
};

export const loadInitialOptions = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return async (onStateChange?: (state: SelectFilterState<ID>) => void): Promise<SelectFilterMonad<ID>> => {
		if (state.didMount) {
			const newDropdownState = {
				...state,
			} satisfies SelectFilterState<ID>;

			onStateChange?.(newDropdownState);

			return SelectFilter({
				initial,
				state: newDropdownState,
				config,
			});
		}

		const newOptions = [...state.initialOptions.options];
		const fetcher = initial.initialOptions?.fetcher;

		if (fetcher) {
			const fetchingInitialOptionsState = {
				...state,
				initialOptions: {
					...state.initialOptions,
					isLoading: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(fetchingInitialOptionsState);

			try {
				const fetchedOptions = await fetcher(config);
				fetchedOptions.forEach((option) => {
					const mappedOption = {
						isSelected: false,
						trackingKey: option.trackingKey,
						value: option.value,
						queryParamValue: option.queryParamValue,
						isSuggested: true,
						isDefault: option.isDefault ?? false,
						avatarUrl: option.avatarUrl,
						label: option.label,
					} satisfies SelectFilterOption;
					newOptions.push(mappedOption);
				});
			} catch {
				const fetchFailedState = {
					...state,
					initialOptions: {
						...state.initialOptions,
						isLoading: false,
					},
				} satisfies SelectFilterState<ID>;
				onStateChange?.(fetchFailedState);
			}
		}

		const newInitialOptionsState = {
			...state,
			didMount: true,
			initialOptions: {
				...state.initialOptions,
				options: newOptions,
				isLoading: false,
			},
		} satisfies SelectFilterState<ID>;

		onStateChange?.(newInitialOptionsState);

		return SelectFilter({
			initial,
			state: newInitialOptionsState,
			config,
		});
	};
};

// TODO: >>>> Write more tests here on edge cases
export const getOptions = <ID extends string>({ initial, state }: SelectFilterDeps<ID>) => {
	return (options: { isLookupActive: boolean }): {
        avatarUrl?: string;
        isDefault: boolean;
        isSelected: boolean;
        isSuggested: boolean;
        label: string;
        queryParamValue: string;
        trackingKey: string;
        value: string;
    }[] => {
		const allSelectedValues = new Set([
			...state.queryParams.options
				.filter((option) => option.isSelected)
				.map((option) => option.value),
			...state.initialOptions.options
				.filter((option) => option.isSelected)
				.map((option) => option.value),
			...state.lookup.options
				.filter((lookupOption) => lookupOption.isSelected)
				.map((option) => option.value),
		]);

		if (options.isLookupActive) {
			const lookupResults = state.lookup.lookupResultOptions.map(
				(option) =>
					({
						...option,
						isSelected: allSelectedValues.has(option.value),
					}) satisfies SelectFilterOption,
			);

			return lookupResults;
		}

		const duplicateOptions = new Set();

		const newOptions = [
			...state.queryParams.options, // Query Params first
			// 'interacted' lookup options are options that a user made a lookup for and has either
			// selected or deselected an option from a lookup query
			// The 'interacted' options are persisted in  state.lookup.options
			...state.lookup.options,
			...state.initialOptions.options,
		]
			.filter(
				(option) => !duplicateOptions.has(option.value) && duplicateOptions.add(option.value), // remove duplicates
			)
			.map((option) => ({
				...option,
				isSelected: allSelectedValues.has(option.value),
			}));

		if (initial.sortCompareFn) {
			return newOptions.sort(initial.sortCompareFn);
		}

		return newOptions;
	};
};

export const setOption = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return (option: SelectFilterOptionChange | SelectFilterOptionChange[]): SelectFilterMonad<ID> => {
		const optionsToChange = Array.isArray(option) ? option : [option];
		const optionsToChangeRecord = Object.fromEntries(
			optionsToChange.map((option) => [option.value, option]),
		) as Record<string, SelectFilterOptionChange | undefined>;

		const queryParamOptions = state.queryParams.options.map((option) => {
			if (!optionsToChange.find((x) => x.value === option.value)) {
				return option;
			}
			return {
				...option,
				isSelected: optionsToChangeRecord[option.value]?.isSelected ?? false,
			};
		});

		const initialOptions = state.initialOptions.options.map((option) => {
			if (!optionsToChange.find((x) => x.value === option.value)) {
				return option;
			}

			return {
				...option,
				isSelected: optionsToChangeRecord[option.value]?.isSelected ?? false,
			};
		});

		const duplicateOptions = new Set();

		const lookupOptions = [
			...state.lookup.options,
			// 'interacted' lookup options are options that a user made a lookup for and has either
			// selected or deselected an option from a lookup query
			// The 'interacted' options are persisted in  state.lookup.options
			...state.lookup.lookupResultOptions.flatMap((option) => {
				if (!optionsToChange.find((x) => x.value === option.value)) {
					return [];
				}

				return [option];
			}),
		]
			.filter(
				(option) => !duplicateOptions.has(option.value) && duplicateOptions.add(option.value), // remove duplicates
			)
			.map((option) => ({
				...option,
				isSuggested: true,
				isSelected: optionsToChangeRecord[option.value]?.isSelected ?? option.isSelected,
			}));

		return SelectFilter({
			initial: initial satisfies CreateSelectFilter<ID>,
			state: {
				...state,
				initialOptions: {
					...state.initialOptions,
					options: initialOptions,
				},
				queryParams: {
					...state.queryParams,
					options: queryParamOptions,
				},
				lookup: {
					...state.lookup,
					options: lookupOptions,
				},
			},
			config,
		});
	};
};

export const clearOptions = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return (): SelectFilterMonad<ID> => {
		const queryParamOptions = state.queryParams.options.map((option) => {
			const isSuggested =
				state.initialOptions.options.find((x) => x.value === option.value)?.isSuggested ?? false;

			return {
				...option,
				isSuggested,
				isSelected: false,
			};
		});

		const initialOptions = state.initialOptions.options.map((option) => ({
			...option,
			isSelected: false,
		}));

		const lookupOptions = state.lookup.options.map((option) => ({
			...option,
			isSelected: false,
		}));

		return SelectFilter({
			initial,
			state: {
				...state,
				initialOptions: {
					...state.initialOptions,
					options: initialOptions,
				},
				queryParams: {
					...state.queryParams,
					options: queryParamOptions,
				},
				lookup: {
					...state.lookup,
					options: lookupOptions,
				},
			},
			config,
		});
	};
};

export const setLookupInput = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return async (query: string, onStateChange?: (state: SelectFilterState<ID>) => void): Promise<SelectFilterMonad<ID>> => {
		if (!initial.lookup || query.length === 0 || state.lookup.input === query) {
			const noQueryState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					isLoading: false,
					isActive: false,
				},
			} satisfies SelectFilterState<ID>;
			onStateChange?.(noQueryState);

			return SelectFilter({
				initial,
				state: noQueryState,
				config,
			});
		}

		const fetchingLookupOptionsState = {
			...state,
			lookup: {
				...state.lookup,
				input: query,
				isLoading: true,
				isActive: true,
			},
		} satisfies SelectFilterState<ID>;

		onStateChange?.(fetchingLookupOptionsState);

		try {
			const fetchedLookupOptions = await initial.lookup.fetcher(query, config);
			const newLookupOptions = fetchedLookupOptions.map((option) => {
				const mappedOption = {
					isSelected: false,
					trackingKey: option.trackingKey,
					value: option.value,
					queryParamValue: option.queryParamValue,
					isSuggested: false,
					isDefault: option.isDefault ?? false,
					avatarUrl: option.avatarUrl,
					label: option.label,
				} satisfies SelectFilterOption;
				return mappedOption;
			});

			const newLookupOptionsState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					lookupResultOptions: newLookupOptions,
					isLoading: false,
					isActive: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(newLookupOptionsState);

			return SelectFilter({
				initial,
				state: newLookupOptionsState,
				config,
			});
		} catch {
			const failedLookupOptionsState = {
				...state,
				lookup: {
					...state.lookup,
					input: query,
					isLoading: false,
					isActive: true,
				},
			} satisfies SelectFilterState<ID>;

			onStateChange?.(failedLookupOptionsState);

			return SelectFilter({
				initial,
				state: failedLookupOptionsState,
				config,
			});
		}
	};
};

export const getValue = ({ initial, state, config }: SelectFilterDepParams) => {
	return (): string[] => {
		return SelectFilter({ initial, state, config })
			.getOptions({ isLookupActive: false })
			.filter((option) => option.isSelected)
			.map((option) => option.value);
	};
};

export const deselectOptions = <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => {
	return (): SelectFilterMonad<ID> => {
		const filter = SelectFilter({ initial, state, config });

		const allOptions = [
			...filter.getOptions({
				isLookupActive: true,
			}),
			...filter.getOptions({
				isLookupActive: false,
			}),
		].map(
			(option) =>
				({
					value: option.value,
					isSelected: false,
				}) satisfies SelectFilterOptionChange,
		);

		return filter.setOption(allOptions);
	};
};
